import { Temporal } from "temporal-polyfill";

import type {
  ResolvedTradingViewChartProps,
  TradingViewBar,
  TradingViewChartProps,
  TradingViewDatafeedConfig,
  TradingViewRawBar,
} from "./TradingViewChart.types";
import {
  DAILY_RESOLUTION,
  DELAYED_MARKET_DATA_MS,
  MARKET_TIMEZONE,
  STOCK_DAILY_HISTORY_DELAY_DAYS,
  TRADING_VIEW_DEFAULTS,
  TRADING_VIEW_RESOLUTION_TIMEFRAMES,
} from "./tradingView.constants";

const EMPTY_OPEN_LIMIT_ORDERS: TradingViewChartProps["openLimitOrders"] = [];
const EMPTY_HISTORICAL_ORDER_FILLS: TradingViewChartProps["historicalOrderFills"] =
  [];

interface FormatChartHistoryEndDateParams {
  periodToMs: number;
  resolution: string;
  crypto: boolean;
  isUnlimited: boolean;
  nowMs?: number;
}

export const formatStockDailyHistoryEndDate = (toMs: number): string => {
  const targetDate = Temporal.Instant.fromEpochMilliseconds(toMs)
    .toZonedDateTimeISO("UTC")
    .subtract({ days: STOCK_DAILY_HISTORY_DELAY_DAYS })
    .toPlainDate();

  return targetDate
    .toZonedDateTime({
      timeZone: MARKET_TIMEZONE,
      plainTime: Temporal.PlainTime.from("00:00"),
    })
    .toString({ timeZoneName: "never" });
};

export const formatChartHistoryEndDate = ({
  periodToMs,
  resolution,
  crypto,
  isUnlimited,
  nowMs = Date.now(),
}: FormatChartHistoryEndDateParams): string => {
  const cappedToMs = Math.min(periodToMs, nowMs);

  if (resolution === DAILY_RESOLUTION && !crypto) {
    return formatStockDailyHistoryEndDate(cappedToMs);
  }

  const delayMs = isUnlimited ? 0 : DELAYED_MARKET_DATA_MS;
  return new Date(cappedToMs - delayMs).toISOString();
};

export const formatBars = (
  barData: TradingViewRawBar[] | null | undefined,
): TradingViewBar[] => {
  if (!barData?.length) {
    return [];
  }
  return barData.map((bar) => ({
    ...bar,
    time: Date.parse(bar.t),
    open: bar.o,
    close: bar.c,
    volume: bar.v,
    high: bar.h,
    low: bar.l,
  }));
};

export const deduplicateBars = (bars: TradingViewBar[]): TradingViewBar[] => {
  const seen = new Set<number>();
  return bars.filter((bar) => {
    if (seen.has(bar.time)) {
      return false;
    }
    seen.add(bar.time);
    return true;
  });
};

export const resolutionToTimeframe = (resolution: string): string | undefined =>
  TRADING_VIEW_RESOLUTION_TIMEFRAMES[resolution];

export const resolveTradingViewChartProps = (
  props: TradingViewChartProps,
): ResolvedTradingViewChartProps => ({
  symbol: props.symbol ?? TRADING_VIEW_DEFAULTS.symbol,
  interval: props.interval ?? TRADING_VIEW_DEFAULTS.interval,
  enabledFeatures: props.enabledFeatures,
  disabledFeatures: props.disabledFeatures,
  theme: props.theme ?? TRADING_VIEW_DEFAULTS.theme,
  datafeedUrl: props.datafeedUrl ?? TRADING_VIEW_DEFAULTS.datafeedUrl,
  libraryPath: props.libraryPath ?? TRADING_VIEW_DEFAULTS.libraryPath,
  chartsStorageUrl:
    props.chartsStorageUrl ?? TRADING_VIEW_DEFAULTS.chartsStorageUrl,
  chartsStorageApiVersion:
    props.chartsStorageApiVersion ??
    TRADING_VIEW_DEFAULTS.chartsStorageApiVersion,
  clientId: props.clientId ?? TRADING_VIEW_DEFAULTS.clientId,
  userId: props.userId ?? TRADING_VIEW_DEFAULTS.userId,
  fullscreen: props.fullscreen ?? TRADING_VIEW_DEFAULTS.fullscreen,
  container: props.container ?? TRADING_VIEW_DEFAULTS.container,
  height: props.height,
  width: props.width ?? TRADING_VIEW_DEFAULTS.width,
  baseUrl: props.baseUrl ?? TRADING_VIEW_DEFAULTS.baseUrl,
  crypto: props.crypto ?? TRADING_VIEW_DEFAULTS.crypto,
  cryptoDataApiUrl: props.cryptoDataApiUrl ?? window.env.CRYPTO_DATA_API_URL,
  seriesStyle: props.seriesStyle ?? TRADING_VIEW_DEFAULTS.seriesStyle,
  utcOffset: props.utcOffset ?? TRADING_VIEW_DEFAULTS.utcOffset,
  timezone: props.timezone ?? TRADING_VIEW_DEFAULTS.timezone,
  exchange: props.exchange ?? TRADING_VIEW_DEFAULTS.exchange,
  isUnlimited: props.isUnlimited ?? TRADING_VIEW_DEFAULTS.isUnlimited,
  isOvernightTradable:
    props.isOvernightTradable ?? TRADING_VIEW_DEFAULTS.isOvernightTradable,
  autosize: props.autosize ?? TRADING_VIEW_DEFAULTS.autosize,
  // Copied so the shared default object can never be mutated by one chart and
  // observed by another. Supplied overrides are passed through by reference,
  // matching v2.
  studiesOverrides: props.studiesOverrides ?? {
    ...TRADING_VIEW_DEFAULTS.studiesOverrides,
  },
  dateRange: props.dateRange,
  savedState: props.savedState,
  onStateChange: props.onStateChange,
  onSymbolChange: props.onSymbolChange,
  comparisonSymbols: props.comparisonSymbols,
  onComparisonSymbolsChange: props.onComparisonSymbolsChange,
  onContextMenu: props.onContextMenu,
  onOrderOverlaySelect: props.onOrderOverlaySelect,
  openLimitOrders: props.openLimitOrders ?? EMPTY_OPEN_LIMIT_ORDERS,
  historicalOrderFills:
    props.historicalOrderFills ?? EMPTY_HISTORICAL_ORDER_FILLS,
});

/**
 * Picks a TradingView resolution for a calendar date-range span so long
 * windows do not request minute bars.
 */
export const resolveIntervalForRange = (spanDays: number): string =>
  spanDays <= 2 ? "1" : spanDays <= 7 ? "15" : spanDays <= 30 ? "60" : "1D";

/**
 * Converts inclusive `YYYY-MM-DD` calendar dates to a TradingView
 * `setVisibleRange` payload in unix seconds (from start-of-day UTC, to
 * end-of-day UTC).
 */
export const workspaceDateRangeToUnixSeconds = (range: {
  from: string;
  to: string;
}): { from: number; to: number } => {
  const fromMs = Date.parse(`${range.from}T00:00:00.000Z`);
  const toMs = Date.parse(`${range.to}T23:59:59.999Z`);
  return {
    from: Math.floor(fromMs / 1000),
    to: Math.floor(toMs / 1000),
  };
};

/** Inclusive day count between two `YYYY-MM-DD` dates (same day → 1). */
export const workspaceDateRangeSpanDays = (range: {
  from: string;
  to: string;
}): number => {
  const fromMs = Date.parse(`${range.from}T00:00:00.000Z`);
  const toMs = Date.parse(`${range.to}T00:00:00.000Z`);
  return Math.max(1, Math.round((toMs - fromMs) / 86_400_000) + 1);
};

export const toTradingViewDatafeedConfig = (
  resolved: ResolvedTradingViewChartProps,
): TradingViewDatafeedConfig => ({
  baseUrl: resolved.baseUrl,
  crypto: resolved.crypto,
  cryptoDataApiUrl: resolved.cryptoDataApiUrl,
  exchange: resolved.exchange,
  utcOffset: resolved.utcOffset,
  isUnlimited: resolved.isUnlimited,
  isOvernightTradable: resolved.isOvernightTradable,
});
